import { type WorkingHoursEntry, intersectIntervals, workingIntervalUtc } from "@ondetatu/shared";
import { useEffect, useMemo, useState } from "react";
import { viewsApi } from "../../lib/api/views.js";
import { useViewState } from "../../lib/view-state.js";
import "./WorkingHours.css";

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function viewerHour(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(utcMs));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return h + m / 60;
}

interface Row {
  entry: WorkingHoursEntry;
  startMs: number;
  endMs: number;
  startH: number;
  endH: number;
}

export function WorkingHoursView() {
  const { asOf } = useViewState();
  const [entries, setEntries] = useState<WorkingHoursEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    viewsApi.workingHours().then((r) => {
      if (r.success && r.data) {
        setEntries(r.data.entries);
        setSelected(new Set(r.data.entries.map((e) => e.user_id)));
      }
    });
  }, []);

  const rows = useMemo<Row[]>(() => {
    return entries
      .filter((e) => e.working_start && e.working_end && e.timezone)
      .map((e) => {
        const iv = workingIntervalUtc(
          asOf,
          e.working_start as string,
          e.working_end as string,
          e.timezone as string,
        );
        const startH = viewerHour(iv.startMs, VIEWER_TZ);
        let endH = viewerHour(iv.endMs, VIEWER_TZ);
        if (endH <= startH) endH = 24; // clamp midnight-wrap for the bar
        return { entry: e, startMs: iv.startMs, endMs: iv.endMs, startH, endH };
      });
  }, [entries, asOf]);

  const overlap = useMemo(() => {
    const chosen = rows.filter((r) => selected.has(r.entry.user_id));
    if (chosen.length < 2) return null;
    const iv = intersectIntervals(chosen.map((r) => ({ startMs: r.startMs, endMs: r.endMs })));
    if (!iv) return null;
    const s = viewerHour(iv.startMs, VIEWER_TZ);
    let e = viewerHour(iv.endMs, VIEWER_TZ);
    if (e <= s) e = 24;
    return { s, e };
  }, [rows, selected]);

  function toggle(id: string) {
    setSelected((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section className="wh">
      <h1 className="wh__h1">Working hours</h1>
      <p className="wh__sub">Times shown in your timezone ({VIEWER_TZ}).</p>

      <div className="wh__axis" aria-hidden="true">
        {[0, 6, 12, 18, 24].map((h) => (
          <span key={h} className="wh__tick" style={{ left: `${(h / 24) * 100}%` }}>
            {h}:00
          </span>
        ))}
      </div>

      <ul className="wh__rows">
        {rows.map((r) => (
          <li key={r.entry.user_id} className="wh__row">
            <label className="wh__name">
              <input
                type="checkbox"
                checked={selected.has(r.entry.user_id)}
                onChange={() => toggle(r.entry.user_id)}
              />
              {r.entry.name}
            </label>
            <div className="wh__track">
              {overlap && (
                <span
                  className="wh__overlap"
                  style={{
                    left: `${(overlap.s / 24) * 100}%`,
                    width: `${((overlap.e - overlap.s) / 24) * 100}%`,
                  }}
                />
              )}
              <span
                className="wh__bar"
                style={{
                  left: `${(r.startH / 24) * 100}%`,
                  width: `${((r.endH - r.startH) / 24) * 100}%`,
                }}
              />
            </div>
          </li>
        ))}
      </ul>

      {overlap ? (
        <p className="wh__result">
          Overlap:{" "}
          <b>
            {Math.floor(overlap.s)}:00–{Math.ceil(overlap.e)}:00
          </b>{" "}
          your time.
        </p>
      ) : (
        <p className="wh__muted">No common working window for the selected people.</p>
      )}
    </section>
  );
}
